import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, InputAdornment, IconButton, TextField } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { getIn } from 'formik';
import { withCapsLockListener } from '../../utils/caps-lock-utils';
import { styled } from '@mui/system';

const CAPS_LOCK_WARNING = 'CapsLock is pressed!';

const StyledTextField = styled(TextField)(({ noWidthLimit, inline, small, medium }) => ({
  ...(noWidthLimit ? {} : { maxWidth: '600px' }),
  width: '100%',
  ...(inline && { width: '100%' }),
  ...(small && { width: '100px' }),
  ...(medium && { width: '280px' }),
  '& input': {
    ...(inline && { width: '100%' }),
    ...(small && { width: '100px' }),
    ...(medium && { width: '280px' }),
  },
  '& input:-webkit-autofill': {
    transition: 'background-color 5000s ease-in-out 0s',
  },
}));

const CommonInlineInputDiv = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'inline-flex',
  [theme.breakpoints.down('sm')]: {
    display: 'block',
  },
}));

const CommonInputWrapper = styled('div')({
  padding: '1.618em',
  display: 'flex',
  justifyContent: 'center',
});

const RenderTextField = ({
  label,
  helperText,
  validate,
  style,
  type,
  input,
  meta,
  disabled,
  placeholder,
  startAdornment,
  endAdornment,
  form,
  field,
  onBlur,
  onChange,
  isSubmitted,
  autoFocus,
  noWidthLimit,
  showAsterisk,
  ...rest
}) => {
  const [passwordToggled, setPasswordToggled] = useState(type !== 'password');

  const touched = form ? (getIn(form.touched, field.name) || isSubmitted) : meta.touched;
  const error = form ? (getIn(form.errors, field.name) || getIn(form.status, field.name)) : meta.error;

  const isPasswordField = type === 'password';
  const isNumberField = type === 'number';

  const inputProps = isNumberField
    ? {
      startAdornment,
      endAdornment,
      inputProps: {
        ...rest,
        form,
        style: { textAlign: 'right' },
        autoComplete: 'off',
        maxLength: 6,
      },
    }
    : {
      startAdornment,
      endAdornment: renderEndAdornment(),
      inputProps: {
        ...rest,
        autoComplete: 'off',
      },
    };

  const isCapsLockWarning = meta && meta.active && isPasswordField && meta.capsLockOn;
  const helperTextValue = (isCapsLockWarning && CAPS_LOCK_WARNING)
    || (touched && error && (error.value || error))
    || helperText;

  return (
    <Box sx={{
      minWidth: '61.8%',
      display: 'inline-flex',
      flexDirection: 'column',
    }}>
      <StyledTextField
        {...input}
        {...rest}
        {...inputProps}
        {...field}
        autoFocus={autoFocus}
        sx={style}
        onBlur={(event) => {
          if (form) {
            form.setFieldTouched(field.name, true);
          } else {
            input.onBlur();
          }
          if (onBlur) {
            onBlur(event);
          }
        }}
        type={isNumberField || passwordToggled ? 'text' : type}
        onChange={(event) => {
          if (form) {
            form.setFieldValue(field.name, event.target.value);
            if (getIn(form.status, field.name)) {
              form.setStatus();
            }
          } else {
            input.onChange(event.target.value);
          }
          if (onChange) {
            onChange(event);
          }
        }}
        margin="dense"
        error={!isCapsLockWarning && touched && !!error}
        label={!noWidthLimit && (showAsterisk ? `${label} *` : label)}
        helperText={helperTextValue}
        placeholder={placeholder}
        variant="outlined"
        disabled={disabled}
      />
    </Box>
  );

  function renderEndAdornment() {
    return (
      <InputAdornment position="end">
        {renderPasswordToggle()}
        {endAdornment}
      </InputAdornment>
    );
  }

  function renderPasswordToggle() {
    return isPasswordField && (
      <IconButton tabIndex="-1" onClick={() => setPasswordToggled(!passwordToggled)}>
        {passwordToggled ? <Visibility /> : <VisibilityOff />}
      </IconButton>
    );
  }
};

RenderTextField.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  validate: PropTypes.func,
  style: PropTypes.object,
  type: PropTypes.string,
  input: PropTypes.object,
  meta: PropTypes.object,
  disabled: PropTypes.bool,
  placeholder: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.object]),
  startAdornment: PropTypes.element,
  endAdornment: PropTypes.element,
  form: PropTypes.object,
  field: PropTypes.object,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  isSubmitted: PropTypes.bool,
  autoFocus: PropTypes.bool,
  noWidthLimit: PropTypes.bool,
  showAsterisk: PropTypes.bool,
};

export default withCapsLockListener(RenderTextField);
